import { z } from "zod";

import type { InferenceModelType } from "@kuzco/models";

import { TEAM_IDS } from "../constants/TeamIds";
import { ZodBigInt } from "../types/ZodBigInt";

function parseBigIntEnvVar(key: string, defaultValue: bigint): bigint {
  return z
    .bigint()
    .parse(
      BigInt(
        (typeof process !== "undefined" ? process.env[key] : undefined) ??
          defaultValue,
      ),
    );
}

/**
 * Token pricing in hogans per 1 token.
 * 10_000 = $0.01 per 1M tokens
 */
export type TokenPricing = {
  promptTokenPrice: bigint;
  completionTokenPrice: bigint;
};

// Pricing Calculator: https://docs.google.com/spreadsheets/d/1j3nlfNeAewYlRLXsYCIc5IU1ZAvRPwFXO_lRCIgBwKc
export const MODEL_PRICE_PER_TOKEN_IN_HOGANS_MAP: Partial<
  Record<string, TokenPricing>
> = {
  "meta-llama/llama-3.1-8b-instruct/q4-k-m": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_1_8B_INSTRUCT_PRICE_IN_HOGANS_Q4_K_M", 10_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_1_8B_INSTRUCT_PRICE_IN_HOGANS_Q4_K_M", 10_000n),
    ),
  },

  "meta-llama/llama-3.1-8b/fp-8": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_1_8B_PRICE_IN_HOGANS", 50_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_1_8B_PRICE_IN_HOGANS", 50_000n),
    ),
  },

  "meta-llama/llama-3.1-70b/fp-8": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_1_70B_PRICE_IN_HOGANS", 300_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_1_70B_PRICE_IN_HOGANS", 300_000n),
    ),
  },

  "meta-llama/llama-3.1-8b-instruct/fp-8": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_1_8B_INSTRUCT_PRICE_IN_HOGANS_FP_8", 25_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_1_8B_INSTRUCT_PRICE_IN_HOGANS_FP_8", 25_000n),
    ),
  },

  "meta-llama/llama-3.1-8b-instruct/fp-16": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_1_8B_INSTRUCT_PRICE_IN_HOGANS", 20_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_1_8B_INSTRUCT_PRICE_IN_HOGANS", 30_000n),
    ),
  },

  "meta-llama/llama-3.1-70b-instruct/fp-16": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "LLAMA3_1_70B_INSTRUCT_FP_16_PRICE_IN_HOGANS",
        300_000n,
      ),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "LLAMA3_1_70B_INSTRUCT_FP_16_PRICE_IN_HOGANS",
        400_000n,
      ),
    ),
  },

  "meta-llama/llama-3.1-70b-instruct/fp-8": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_1_70B_INSTRUCT_PRICE_IN_HOGANS", 300_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_1_70B_INSTRUCT_PRICE_IN_HOGANS", 300_000n),
    ),
  },

  "meta-llama/llama-3.3-70b-instruct/fp-16": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "LLAMA3_3_70B_INSTRUCT_FP_16_PRICE_IN_HOGANS",
        300_000n,
      ),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "LLAMA3_3_70B_INSTRUCT_FP_16_PRICE_IN_HOGANS",
        400_000n,
      ),
    ),
  },

  "meta-llama/llama-3.3-70b-instruct/fp-8": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_3_70B_INSTRUCT_FP_8_PRICE_IN_HOGANS", 100_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_3_70B_INSTRUCT_FP_8_PRICE_IN_HOGANS", 250_000n),
    ),
  },

  "meta-llama/llama-3.2-1b-instruct/fp-8": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_2_1B_INSTRUCT_PRICE_IN_HOGANS", 10_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_2_1B_INSTRUCT_PRICE_IN_HOGANS", 10_000n),
    ),
  },

  "meta-llama/llama-3.2-3b-instruct/fp-8": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_2_3B_INSTRUCT_PRICE_IN_HOGANS", 20_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_2_3B_INSTRUCT_PRICE_IN_HOGANS", 20_000n),
    ),
  },

  "meta-llama/llama-3.2-1b-instruct/fp-16": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_2_1B_INSTRUCT_PRICE_IN_HOGANS", 10_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_2_1B_INSTRUCT_PRICE_IN_HOGANS", 10_000n),
    ),
  },

  "meta-llama/llama-3.2-3b-instruct/fp-16": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_2_3B_INSTRUCT_PRICE_IN_HOGANS", 20_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_2_3B_INSTRUCT_PRICE_IN_HOGANS", 20_000n),
    ),
  },

  "mistralai/mistral-nemo-12b-instruct/fp-8": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "MISTRAL_NEMO_12B_8BIT_INSTRUCT_PRICE_IN_HOGANS",
        37_500n,
      ), // $0.0375
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "MISTRAL_NEMO_12B_8BIT_INSTRUCT_PRICE_IN_HOGANS",
        100_000n,
      ), // $0.1
    ),
  },

  "meta-llama/llama-3.2-11b-instruct/fp-16": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_2_11B_FP_16_INSTRUCT_PRICE_IN_HOGANS", 55_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("LLAMA3_2_11B_FP_16_INSTRUCT_PRICE_IN_HOGANS", 55_000n),
    ),
  },

  "RLAIF/sft-llama-3.1-8b-external/fp-16": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "RLAIF_SFT_LLAMA_3_1_8B_FP16_EXTERNAL_PRICE_IN_HOGANS",
        10_000n,
      ),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "RLAIF_SFT_LLAMA_3_1_8B_FP16_EXTERNAL_PRICE_IN_HOGANS",
        10_000n,
      ),
    ),
  },

  "deepseek/deepseek-v3/fp-8": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("DEEPSEEK_V3_FP_8_PRICE_IN_HOGANS", 400_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("DEEPSEEK_V3_FP_8_PRICE_IN_HOGANS", 1_200_000n),
    ),
  },

  "deepseek/deepseek-v3-0324/fp-8": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("DEEPSEEK_V3_0324_FP_8_PRICE_IN_HOGANS", 450_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("DEEPSEEK_V3_0324_FP_8_PRICE_IN_HOGANS", 1_450_000n),
    ),
  },

  // $0.15 / 1M prompt tokens, $0.30 / 1M completion tokens.
  "deepseek/deepseek-v4-flash": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("DEEPSEEK_V4_FLASH_PROMPT_PRICE_IN_HOGANS", 150_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "DEEPSEEK_V4_FLASH_COMPLETION_PRICE_IN_HOGANS",
        300_000n,
      ),
    ),
  },

  "deepseek/deepseek-r1/fp-8": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("DEEPSEEK_R1_FP_8_PRICE_IN_HOGANS", 450_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("DEEPSEEK_R1_FP_8_PRICE_IN_HOGANS", 2_150_000n),
    ),
  },

  "deepseek/deepseek-r1-0528/fp-8": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("DEEPSEEK_R1_0528_FP_8_PRICE_IN_HOGANS", 500_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("DEEPSEEK_R1_0528_FP_8_PRICE_IN_HOGANS", 2_150_000n),
    ),
  },

  "qwen/qwen2.5-72b-instruct/fp-8": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("QWEN_2_5_72B_INSTRUCT_FP_8_PRICE_IN_HOGANS", 350_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("QWEN_2_5_72B_INSTRUCT_FP_8_PRICE_IN_HOGANS", 350_000n),
    ),
  },

  "nous/hermes-3-llama-3.1-405b/fp-8": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "HERMES_3_LLAMA_3_1_405B_FP_8_PRICE_IN_HOGANS",
        800_000n,
      ),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "HERMES_3_LLAMA_3_1_405B_FP_8_PRICE_IN_HOGANS",
        800_000n,
      ),
    ),
  },

  "thedrummer/unslopnemo-12b-v4.1/bf-16": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("UNSLOPNEMO_12B_V4_1_BF_16_PRICE_IN_HOGANS", 500_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("UNSLOPNEMO_12B_V4_1_BF_16_PRICE_IN_HOGANS", 500_000n),
    ),
  },

  "thedrummer/rocinante-12b-v1.1/bf-16": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("ROCINANTE_12B_V1_1_BF_16_PRICE_IN_HOGANS", 800_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("ROCINANTE_12B_V1_1_BF_16_PRICE_IN_HOGANS", 800_000n),
    ),
  },

  "gryphe/mythomax-l2-13b/bf-16": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("MYTHOMAX_L2_13B_BF_16_PRICE_IN_HOGANS", 190_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("MYTHOMAX_L2_13B_BF_16_PRICE_IN_HOGANS", 190_000n),
    ),
  },

  "deepseek/r1-distill-llama-70b/fp-8": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("R1_DISTILL_LLAMA_70B_FP_8_PRICE_IN_HOGANS", 100_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("R1_DISTILL_LLAMA_70B_FP_8_PRICE_IN_HOGANS", 400_000n),
    ),
  },

  "google/gemma-3-27b-instruct/bf-16": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("GEMMA_3_27B_INSTRUCT_BF_16_PRICE_IN_HOGANS", 150_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("GEMMA_3_27B_INSTRUCT_BF_16_PRICE_IN_HOGANS", 300_000n),
    ),
  },

  "qwen/qwen2.5-7b-instruct/bf-16": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("QWEN_2_5_7B_INSTRUCT_BF_16_PRICE_IN_HOGANS", 200_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("QWEN_2_5_7B_INSTRUCT_BF_16_PRICE_IN_HOGANS", 200_000n),
    ),
  },

  "qwen/qwq-32b/fp-8": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("QWEN_QWQ_32B_FP_8_PRICE_IN_HOGANS", 200_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("QWEN_QWQ_32B_FP_8_PRICE_IN_HOGANS", 200_000n),
    ),
  },
  "qwen/qwen3-30b-a3b/fp8": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("QWEN_3_30B_A3B_FP8_PRICE_IN_HOGANS", 80_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("QWEN_3_30B_A3B_FP8_PRICE_IN_HOGANS", 290_000n),
    ),
  },
  "tenacity-labs/magneta-chat-v3": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "TENACITY_LABS_MAGNETA_CHAT_V3_PRICE_IN_HOGANS",
        1_000n,
      ),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "TENACITY_LABS_MAGNETA_CHAT_V3_PRICE_IN_HOGANS",
        1_000n,
      ),
    ),
  },

  "deepseek/deepseek-vl2-tiny/fp-16": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("DEEPSEEK_VL2_TINY_FP16_PRICE_IN_HOGANS", 50_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("DEEPSEEK_VL2_TINY_FP16_PRICE_IN_HOGANS", 100_000n),
    ),
  },

  "deepseek/deepseek-vl2-small/fp-16": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("DEEPSEEK_VL2_SMALL_FP16_PRICE_IN_HOGANS", 100_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("DEEPSEEK_VL2_SMALL_FP16_PRICE_IN_HOGANS", 200_000n),
    ),
  },

  "osmosis-ai/osmosis-structure-0.6b/fp-32": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "OSMOSIS_STRUCTURE_0_6B_FP_32_PRICE_IN_HOGANS",
        100_000n,
      ),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "OSMOSIS_STRUCTURE_0_6B_FP_32_PRICE_IN_HOGANS",
        500_000n,
      ),
    ),
  },

  "qwen/qwen3-embedding-4b": {
    promptTokenPrice: ZodBigInt.parse(
      // $0.01 per 1M tokens
      parseBigIntEnvVar("QWEN_3_EMBEDDING_4B_PRICE_IN_HOGANS", 10_000n),
    ),
    // Embedding models don't produce completion tokens
    completionTokenPrice: ZodBigInt.parse(0n),
  },

  "inference-net/cliptagger-12b": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("CLIP_TAGGER_12B_PRICE_IN_HOGANS", 300_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("CLIP_TAGGER_12B_PRICE_IN_HOGANS", 500_000n),
    ),
  },
  "wynd/wynd-vidcap-12b": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("CLIP_TAGGER_12B_PRICE_IN_HOGANS", 300_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("CLIP_TAGGER_12B_PRICE_IN_HOGANS", 500_000n),
    ),
  },

  "cal-ai/food-vision-27b": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("CAL_AI_FOOD_VISION_27B_PRICE_IN_HOGANS", 250_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("CAL_AI_FOOD_VISION_27B_PRICE_IN_HOGANS", 500_000n),
    ),
  },

  "cal-ai/food-vision-12b": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("CAL_AI_FOOD_VISION_12B_PRICE_IN_HOGANS", 250_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("CAL_AI_FOOD_VISION_12B_PRICE_IN_HOGANS", 500_000n),
    ),
  },

  "cal-ai/food-vision-v3": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("CAL_AI_FOOD_VISION_V3_PRICE_IN_HOGANS", 250_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("CAL_AI_FOOD_VISION_V3_PRICE_IN_HOGANS", 500_000n),
    ),
  },

  "cal-ai/food-vision-v4": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("CAL_AI_FOOD_VISION_V3_PRICE_IN_HOGANS", 1_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("CAL_AI_FOOD_VISION_V3_PRICE_IN_HOGANS", 1_000n),
    ),
  },

  "openai/gpt-oss-120b": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("OPENAI_GPT_OSS_120B_PRICE_IN_HOGANS", 50_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("OPENAI_GPT_OSS_120B_PRICE_IN_HOGANS", 450_000n),
    ),
  },

  "openai/gpt-oss-20b": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("OPENAI_GPT_OSS_20B_PRICE_IN_HOGANS", 30_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("OPENAI_GPT_OSS_20B_PRICE_IN_HOGANS", 150_000n),
    ),
  },

  "inference-net/schematron-3b": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("INFERENCE_NET_SCHEMATRON_3B_PRICE_IN_HOGANS", 20_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("INFERENCE_NET_SCHEMATRON_3B_PRICE_IN_HOGANS", 50_000n),
    ),
  },

  // 5x the schematron-3b price
  "inference-net/schematron-v2-small": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "INFERENCE_NET_SCHEMATRON_V2_SMALL_PRICE_IN_HOGANS",
        50_000n,
      ),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "INFERENCE_NET_SCHEMATRON_V2_SMALL_PRICE_IN_HOGANS",
        250_000n,
      ),
    ),
  },

  // 3x the schematron-3b price
  "inference-net/schematron-v2-turbo": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "INFERENCE_NET_SCHEMATRON_V2_TURBO_PRICE_IN_HOGANS",
        30_000n,
      ),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "INFERENCE_NET_SCHEMATRON_V2_TURBO_PRICE_IN_HOGANS",
        150_000n,
      ),
    ),
  },

  "inference-net/schematron-v3": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("INFERENCE_NET_SCHEMATRON_V3_PRICE_IN_HOGANS", 20_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("INFERENCE_NET_SCHEMATRON_V3_PRICE_IN_HOGANS", 50_000n),
    ),
  },

  "inference-net/schematron-8b": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("INFERENCE_NET_SCHEMATRON_8B_PRICE_IN_HOGANS", 40_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "INFERENCE_NET_SCHEMATRON_8B_PRICE_IN_HOGANS",
        100_000n,
      ),
    ),
  },

  "inference-net/bdc-coder-qwen3-14b": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "INFERENCE_NET_BDC_CODER_QWEN3_14B_PRICE_IN_HOGANS",
        10_000n,
      ),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar(
        "INFERENCE_NET_BDC_CODER_QWEN3_14B_PRICE_IN_HOGANS",
        10_000n,
      ),
    ),
  },

  "inference-net/bdc-coder": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("INFERENCE_NET_BDC_CODER_PRICE_IN_HOGANS", 10_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("INFERENCE_NET_BDC_CODER_PRICE_IN_HOGANS", 10_000n),
    ),
  },

  "profound-ai/profound-entity-recognition": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("PROFOUND_ENTITY_RECOGNITION_PRICE_IN_HOGANS", 10_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("PROFOUND_ENTITY_RECOGNITION_PRICE_IN_HOGANS", 10_000n),
    ),
  },

  "tenacity-labs/magneta-chat": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("TENACITY_LABS_MAGNETA_CHAT_PRICE_IN_HOGANS", 1_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("TENACITY_LABS_MAGNETA_CHAT_PRICE_IN_HOGANS", 1_000n),
    ),
  },

  "gravity/gravity-xs": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("GRAVITY_XS_PRICE_IN_HOGANS", 1_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("GRAVITY_XS_PRICE_IN_HOGANS", 1_000n),
    ),
  },

  "gravity/gravity-s": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("GRAVITY_S_PRICE_IN_HOGANS", 1_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("GRAVITY_S_PRICE_IN_HOGANS", 1_000n),
    ),
  },

  "inference-net/load-test": {
    // Default lowered from 10_000n -> 1n so dev / inference.cool e2e
    // validation against the load-test model only consumes ~1 hogan/token
    // (~1 nanocredit per token after ceiling-rounding). Still configurable
    // via INFERENCE_NET_LOAD_TEST_PRICE_IN_HOGANS if a future load test
    // wants more realistic numbers.
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("INFERENCE_NET_LOAD_TEST_PRICE_IN_HOGANS", 1n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("INFERENCE_NET_LOAD_TEST_PRICE_IN_HOGANS", 1n),
    ),
  },

  "rizz/rizz-chat": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("RIZZ_CHAT_PRICE_IN_HOGANS", 1_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("RIZZ_CHAT_PRICE_IN_HOGANS", 1_000n),
    ),
  },

  "nvidia/nemotron3-super": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("NVIDIA_NEMOTRON3_SUPER_PRICE_IN_HOGANS", 2_500_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("NVIDIA_NEMOTRON3_SUPER_PRICE_IN_HOGANS", 5_000_000n),
    ),
  },

  "cal-ai/food-vision-v5": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("CAL_AI_FOOD_VISION_V5_PRICE_IN_HOGANS", 1_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("CAL_AI_FOOD_VISION_V5_PRICE_IN_HOGANS", 1_000n),
    ),
  },

  "olive/analyze-product-v1": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("OLIVE_ANALYZE_PRODUCT_V1_PRICE_IN_HOGANS", 1n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("OLIVE_ANALYZE_PRODUCT_V1_PRICE_IN_HOGANS", 1n),
    ),
  },

  "gravity/gravity-v2": {
    promptTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("GRAVITY_V2_PRICE_IN_HOGANS", 1_000n),
    ),
    completionTokenPrice: ZodBigInt.parse(
      parseBigIntEnvVar("GRAVITY_V2_PRICE_IN_HOGANS", 1_000n),
    ),
  },
};

export const PRIVATE_MODELS_APPROVED_SET: Partial<
  Record<InferenceModelType, Set<string>>
> = {
  "RLAIF/sft-llama-3.1-8b-external/fp-16": new Set([TEAM_IDS.SYNTHLABS]),

  // Private model: only the system account and internal inference.net teams may
  // call it.
  "deepseek/deepseek-v4-flash": new Set([
    TEAM_IDS.SYSTEM_ACCOUNT,
    TEAM_IDS.ABE_INFERENCE_NET,
    TEAM_IDS.AMAR_INFERENCE_NET,
    TEAM_IDS.HARRY_INFERENCE_NET,
  ]),

  "wynd/wynd-vidcap-12b": new Set([TEAM_IDS.WYND_LABS_GRASS]),
  "cal-ai/food-vision-27b": new Set([TEAM_IDS.CAL_AI]),
  "cal-ai/food-vision-12b": new Set([TEAM_IDS.CAL_AI]),
  "cal-ai/food-vision-v3": new Set([TEAM_IDS.CAL_AI]),
  "cal-ai/food-vision-v5": new Set([TEAM_IDS.CAL_AI, TEAM_IDS.SYSTEM_ACCOUNT]),

  "cal-ai/food-vision-v4": new Set([
    TEAM_IDS.CAL_AI,
    TEAM_IDS.SYSTEM_ACCOUNT,
    TEAM_IDS.HARRY_INFERENCE_NET,
    TEAM_IDS.AMAR_INFERENCE_NET,
    TEAM_IDS.ABE_INFERENCE_NET,
  ]),

  "olive/analyze-product-v1": new Set([
    TEAM_IDS.OLIVE,
    TEAM_IDS.SYSTEM_ACCOUNT,
    TEAM_IDS.HARRY_INFERENCE_NET,
    TEAM_IDS.AMAR_INFERENCE_NET,
    TEAM_IDS.ABE_INFERENCE_NET,
  ]),

  "inference-net/bdc-coder-qwen3-14b": new Set([TEAM_IDS.SYSTEM_ACCOUNT]),
  "inference-net/bdc-coder": new Set([TEAM_IDS.SYSTEM_ACCOUNT]),

  "profound-ai/profound-entity-recognition": new Set([
    TEAM_IDS.SYSTEM_ACCOUNT,
    TEAM_IDS.AMAR_INFERENCE_NET,
    TEAM_IDS.HARRY_INFERENCE_NET,
    TEAM_IDS.SHIVAM_PROFOUND_AI,
  ]),

  "tenacity-labs/magneta-chat": new Set([
    TEAM_IDS.TENACITY_LABS,
    TEAM_IDS.SYSTEM_ACCOUNT,
    TEAM_IDS.HARRY_INFERENCE_NET,
    TEAM_IDS.AMAR_INFERENCE_NET,
    TEAM_IDS.ABE_INFERENCE_NET,
  ]),

  "tenacity-labs/magneta-chat-v3": new Set([
    TEAM_IDS.TENACITY_LABS,
    TEAM_IDS.SYSTEM_ACCOUNT,
    TEAM_IDS.HARRY_INFERENCE_NET,
    TEAM_IDS.AMAR_INFERENCE_NET,
    TEAM_IDS.ABE_INFERENCE_NET,
  ]),

  "gravity/gravity-xs": new Set([
    TEAM_IDS.SYSTEM_ACCOUNT,
    TEAM_IDS.HARRY_INFERENCE_NET,
    TEAM_IDS.AMAR_INFERENCE_NET,
    TEAM_IDS.ABE_INFERENCE_NET,
    TEAM_IDS.GRAVITY_ADS,
  ]),

  "gravity/gravity-s": new Set([
    TEAM_IDS.SYSTEM_ACCOUNT,
    TEAM_IDS.HARRY_INFERENCE_NET,
    TEAM_IDS.AMAR_INFERENCE_NET,
    TEAM_IDS.ABE_INFERENCE_NET,
    TEAM_IDS.GRAVITY_ADS,
  ]),

  "rizz/rizz-chat": new Set([
    TEAM_IDS.SYSTEM_ACCOUNT,
    TEAM_IDS.HARRY_INFERENCE_NET,
    TEAM_IDS.AMAR_INFERENCE_NET,
    TEAM_IDS.ABE_INFERENCE_NET,
    TEAM_IDS.RIZZ,
  ]),
};
